<div class="px-6 py-4 border-b border-gray-100 bg-white flex flex-row justify-between items-start gap-4">
  <div class="flex items-center gap-3 min-w-0">
    <span class="h-11 w-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0"><mat-icon>assignment_return</mat-icon></span>
    <div class="min-w-0">
      <h1 class="text-xl font-bold text-gray-900 leading-tight truncate">Order #{{ orderReturn?.orderNo || orderReturn?.orderId }}</h1>
      <p class="text-xs text-gray-500 truncate">{{ orderReturn?.customerName || 'Return #' + orderReturn?.id }}</p>
    </div>
  </div>
  <button type="button" (click)="close()" class="h-9 w-9 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800 flex items-center justify-center text-sm transition-colors flex-shrink-0" aria-label="Close" matTooltip="Close">✕</button>
</div>

@if (loading && !orderReturn) {
<div class="flex justify-center py-16"><mat-spinner diameter="32"></mat-spinner></div>
}

@if (orderReturn && !loading) {
<mat-dialog-content class="mat-typography !px-6 !py-5 !max-h-[64vh]">

  @if (orderReturn.reason) {
  <div class="mb-5 flex items-start gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-xl px-3.5 py-2.5">
    <mat-icon class="!text-[16px] !h-4 !w-4 mt-0.5 text-gray-400 flex-shrink-0">chat_bubble_outline</mat-icon>{{ orderReturn.reason }}
  </div>
  }

  <!-- Status -->
  <div class="mb-6">
    <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Status</div>
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
      @for (s of statusOptions; track s) {
      <button type="button" class="flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all"
        [class]="orderReturn.status === s ? 'bg-neutral-900 text-white border-neutral-900 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50'"
        (click)="updateStatus(s)">
        <mat-icon class="!text-[16px] !h-4 !w-4" [class]="orderReturn.status === s ? '!text-amber-400' : '!text-gray-400'">{{ statusIcon(s) }}</mat-icon>{{ s }}
      </button>
      }
    </div>
  </div>

  <!-- Summary -->
  <div class="grid grid-cols-3 gap-3 mb-6">
    <div class="rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3">
      <div class="text-[11px] uppercase tracking-wider text-gray-400">Items</div>
      <div class="text-xl font-bold text-gray-900">{{ totalQuantity }}</div>
    </div>
    <div class="rounded-2xl bg-gray-50 border border-gray-100 px-4 py-3">
      <div class="text-[11px] uppercase tracking-wider text-gray-400">Restocked</div>
      <div class="text-xl font-bold text-emerald-600">{{ restockedCount }}</div>
    </div>
    <div class="rounded-2xl px-4 py-3" [class]="restockableCount ? 'bg-amber-50 border border-amber-100' : 'bg-gray-50 border border-gray-100'">
      <div class="text-[11px] uppercase tracking-wider" [class]="restockableCount ? 'text-amber-700' : 'text-gray-400'">Awaiting restock</div>
      <div class="text-xl font-bold" [class]="restockableCount ? 'text-amber-700' : 'text-gray-900'">{{ restockableCount }}</div>
    </div>
  </div>

  <!-- Items -->
  <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Items</div>
  <div class="rounded-2xl border border-gray-100 overflow-hidden">
    <table class="w-full text-sm">
      <thead class="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        <tr>
          <th class="p-3">Product</th>
          <th class="p-3 w-16 text-right">Qty</th>
          <th class="p-3 w-32">Condition</th>
          <th class="p-3 w-56">Restock</th>
        </tr>
      </thead>
      <tbody>
        @for (item of orderReturn.items; track item.id) {
        <tr class="border-t border-gray-100 align-top">
          <td class="p-3">
            <div class="font-medium text-gray-800">{{ item.productName || ('Variant #' + item.productVariantId) }}</div>
            @if (item.sku) { <div class="text-xs text-gray-400 font-mono">{{ item.sku }}</div> }
          </td>
          <td class="p-3 text-right text-gray-700">{{ item.quantity }}</td>
          <td class="p-3">
            <span class="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold" [ngClass]="conditionClass(item.condition)">{{ item.condition }}</span>
          </td>
          <td class="p-3">
            @if (item.restocked) {
              <span class="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><mat-icon class="!text-[16px] !h-4 !w-4">check_circle</mat-icon>In {{ item.warehouse }}</span>
            } @else if (item.condition === 'UNSELLABLE') {
              <span class="text-xs text-gray-400">Unsellable - not restockable</span>
            } @else {
              <div class="flex items-center gap-1.5">
                <select class="px-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 outline-none text-xs" [(ngModel)]="restockWarehouse[item.id]">
                  @for (w of warehouseOptions; track w) { <option [value]="w">{{ w }}</option> }
                </select>
                <button type="button" class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold hover:bg-amber-100 transition-colors disabled:opacity-50" [disabled]="savingItemId === item.id" (click)="restock(item.id)">
                  @if (savingItemId === item.id) { <span class="inline-block h-3 w-3 rounded-full border-2 border-amber-700 border-t-transparent animate-spin"></span> }
                  Restock
                </button>
              </div>
            }
          </td>
        </tr>
        }
      </tbody>
    </table>
  </div>

  <div class="mt-4 text-xs text-gray-400">Raised {{ orderReturn.createdAt | date : 'd MMM y, HH:mm' }} by {{ orderReturn.initiatedBy || 'unknown' }}</div>
</mat-dialog-content>

<div class="px-6 py-4 border-t border-gray-100 bg-white flex flex-row items-center justify-end gap-3">
  <button type="button" (click)="close()" class="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">Close</button>
</div>
}
